import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import useSWR from 'swr'
import { OnboardingLayout } from './OnboardingLayout'
import { AccessCodeStep } from './steps/AccessCodeStep'
import { AccountSetupStep } from './steps/AccountSetupStep'
import { AudienceStep } from './steps/AudienceStep'
import { IndustryStep } from './steps/IndustryStep'
import { MarketsStep } from './steps/MarketsStep'
import { JtbdStep } from './steps/JtbdStep'
import { BrandStep } from './steps/BrandStep'
import { GettingStartedGuide } from './steps/GettingStartedGuide/GettingStartedGuide'
import { usersApi, organizationsApi, populationsApi } from '@/lib/api'
import { AudienceSummary } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import { OnboardingWizardState, OnboardingStep, OnboardingStepId } from '@/types/components/onboarding'
import {
  bookmarkTopAudiences,
  seedInitialFeaturedTwinsFromAudiences,
} from './steps/GettingStartedGuide/scoreAudiences'
import { useAuth } from '@/lib/config/auth-provider'

/**
 * All possible onboarding steps in order.
 * Invited users skip 'brand' since brand is org-level and already
 * configured by the org creator — manageable in Settings.
 */
const ALL_STEPS: Array<OnboardingStepId> = ['access_code', 'account_setup', 'audience', 'industry', 'markets', 'jtbd', 'brand', 'getting_started']

interface WelcomeSequenceProps {
  onComplete: () => void | Promise<void>
  onSkip: () => void
}

export function WelcomeSequence({ onComplete, onSkip }: WelcomeSequenceProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const { user } = useAuth()
  const shouldReduceMotion = useReducedMotion()
  const stepContainerRef = useRef<HTMLDivElement>(null)

  const devInvited =
    process.env.NODE_ENV === 'development' &&
    localStorage.getItem('dev-onboarding-preview') === 'invited'

  const [wizardState, setWizardState] = useState<OnboardingWizardState>({
    currentStep: 0,
    targetingType: 'B2B',
    selectedInterests: [],
    selectedCountries: [],
    otherInterest: '',
    isInvitedUser: devInvited,
    orgName: devInvited ? 'Acme Corp' : '',
    orgId: null,
    selectedIndustries: [],
    selectedIndustryAncestors: [],
    selectedJtbd: [],
  })

  // Prefetch audiences as soon as the wizard opens so the Getting Started
  // Guide's AudiencesSection reads from cache instead of waiting on mount.
  // SWR deduplicates by key — AudiencesSection's own useSWR call will be a
  // cache hit and render immediately.
  useSWR<Array<AudienceSummary>>(
    'home-audiences',
    async () => {
      const list = await populationsApi.list()
      return list.filter((a) => a.status === 'ready')
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  )

  // Show access_code step only for OAuth users (signaled by sessionStorage).
  // Email users already enter their code during registration.
  const [showAccessCodeStep] = useState(
    () => !!sessionStorage.getItem('pending_access_code'),
  )

  const steps = useMemo<Array<OnboardingStepId>>(
    () => ALL_STEPS.filter((id) => {
      if (id === 'brand' && wizardState.isInvitedUser) return false
      if (id === 'access_code' && !showAccessCodeStep) return false
      return true
    }),
    [wizardState.isInvitedUser, showAccessCodeStep],
  )
  const totalSteps = steps.length
  const currentStepId = steps[wizardState.currentStep]

  const [isSaving, setIsSaving] = useState(false)
  const [completionError, setCompletionError] = useState<string | null>(null)
  const [brandOnWideSubStep, setBrandOnWideSubStep] = useState(false)
  const stepStartTime = useRef(Date.now())
  const currentStepRef = useRef(wizardState.currentStep)
  const stepsRef = useRef(steps)
  const targetingTypeRef = useRef(wizardState.targetingType)
  const wizardStateRef = useRef(wizardState)

  useEffect(() => {
    currentStepRef.current = wizardState.currentStep
    stepsRef.current = steps
    targetingTypeRef.current = wizardState.targetingType
    wizardStateRef.current = wizardState
  }, [wizardState, steps])

  useEffect(() => {
    track(AnalyticsEvents.ONBOARDING_STARTED, { targeting_type: targetingTypeRef.current })

    const handleBeforeUnload = () => {
      track(AnalyticsEvents.ONBOARDING_ABANDONED, {
        last_step_index: currentStepRef.current,
        last_step_name: stepsRef.current[currentStepRef.current],
      })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  useEffect(() => {
    organizationsApi
      .getCurrent()
      .then((org) => {
        if (!org) return
        setWizardState((prev) => ({
          ...prev,
          // In dev-invited mode, keep the simulated invited state
          isInvitedUser: devInvited || org.orgRole !== 'owner',
          orgName: devInvited ? prev.orgName : (org.name || ''),
          orgId: org.id,
        }))
      })
      .catch((err: unknown) => {
        console.warn('Failed to fetch current organization:', getApiErrorMessage(err, 'unknown error'))
      })
  }, [devInvited])

  useEffect(() => {
    stepContainerRef.current?.focus()
  }, [wizardState.currentStep])

  // Reset wide layout whenever step changes so brand step re-enters as narrow by default.
  useEffect(() => {
    setBrandOnWideSubStep(false)
  }, [wizardState.currentStep])

  const updateState = useCallback(
    (updates: Partial<OnboardingWizardState>) => {
      setWizardState((prev) => ({ ...prev, ...updates }))
    },
    [],
  )

  const persistStep = useCallback(
    async (step: number, awaited = false) => {
      const call = usersApi.updateOnboardingState({ onboardingStep: step })
      if (awaited) {
        await call
        localStorage.removeItem('showOnboardingTour')
      }
    },
    [],
  )

  const handleNext = useCallback(async () => {
    const durationMs = Date.now() - stepStartTime.current
    track(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, {
      step_name: steps[wizardState.currentStep],
      step_index: wizardState.currentStep,
      skipped: false,
      duration_ms: durationMs,
      targeting_type: wizardState.targetingType,
      selections_count: wizardState.selectedIndustries.length,
    })
    stepStartTime.current = Date.now()

    const nextStep = wizardState.currentStep + 1
    if (nextStep >= totalSteps) {
      track(AnalyticsEvents.ONBOARDING_COMPLETED, {
        total_steps_completed: totalSteps,
        skipped_steps: 0,
        targeting_type: wizardState.targetingType,
        has_brand: !!wizardState.orgName,
      })
      setCompletionError(null)
      setIsSaving(true)
      try {
        const bookmarkedAudiences = await bookmarkTopAudiences(
          wizardStateRef.current,
          user?.featureFlags?.marketplaceAccess !== false,
        )
        await seedInitialFeaturedTwinsFromAudiences(bookmarkedAudiences)
        await onComplete()
      } catch {
        setIsSaving(false)
        setCompletionError(t('completionError'))
      }
      return
    }

    try {
      await persistStep(nextStep, wizardState.currentStep === 0)
      updateState({ currentStep: nextStep as OnboardingStep })
    } catch {
        setCompletionError(t('progressError'))
    }
  }, [steps, totalSteps, wizardState.currentStep, wizardState.targetingType, wizardState.selectedIndustries.length, wizardState.orgName, user?.featureFlags?.marketplaceAccess, onComplete, persistStep, updateState, t])

  const handleSkip = useCallback(async () => {
    track(AnalyticsEvents.ONBOARDING_STEP_COMPLETED, {
      step_name: steps[wizardState.currentStep],
      step_index: wizardState.currentStep,
      skipped: true,
      duration_ms: Date.now() - stepStartTime.current,
    })
    stepStartTime.current = Date.now()

    const nextStep = wizardState.currentStep + 1
    if (nextStep >= totalSteps) {
      setCompletionError(null)
      await usersApi.updateOnboardingState({
        hasCompletedOnboarding: true,
        onboardingStep: -1,
      })
      try {
        await onComplete()
      } catch {
        setCompletionError(t('completionError'))
      }
      return
    }
    persistStep(nextStep)
    updateState({ currentStep: nextStep as OnboardingStep })
  }, [steps, totalSteps, wizardState.currentStep, onComplete, persistStep, updateState, t])

  const duration = shouldReduceMotion ? 0 : 0.3

  const handleBack = useCallback(() => {
    if (wizardState.currentStep > 0) {
      const prevStep = wizardState.currentStep - 1
      persistStep(prevStep)
      updateState({ currentStep: prevStep as OnboardingStep })
    }
  }, [wizardState.currentStep, persistStep, updateState])

  const showBack = wizardState.currentStep > 0

  const handleSubStepChange = useCallback((isWide: boolean) => {
    setBrandOnWideSubStep(isWide)
  }, [])

  const stepProps = {
    wizardState,
    onUpdateState: updateState,
    onNext: handleNext,
    onBack: handleBack,
    onSkip: handleSkip,
  }

  const backButton = showBack ? (
    <button
      type="button"
      onClick={handleBack}
      data-testid="onboarding-back-button"
      className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('back', { ns: 'common' })}
    </button>
  ) : undefined

  // Progress fraction: exclude the final 'getting_started' summary screen
  // so the bar reaches 100% on the last real step.
  const progressSteps = totalSteps - 1
  const progress = progressSteps > 0 ? (wizardState.currentStep / progressSteps) : 0

  return (
    <OnboardingLayout
      topLeft={backButton}
      progress={progress}
      maxWidth={
        currentStepId === 'brand' && brandOnWideSubStep
          ? 'max-w-3xl'
          : currentStepId === 'markets'
            ? 'max-w-lg'
            : currentStepId === 'access_code' ||
                currentStepId === 'account_setup' ||
                currentStepId === 'audience' ||
                currentStepId === 'industry' ||
                currentStepId === 'jtbd' ||
                currentStepId === 'getting_started'
              ? 'max-w-2xl'
              : undefined
      }
    >

      <AnimatePresence mode="wait">
        <motion.div
          key={wizardState.currentStep}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration, ease: 'easeOut' }}
        >
          {currentStepId === 'access_code' && <AccessCodeStep ref={stepContainerRef} {...stepProps} />}
          {currentStepId === 'account_setup' && <AccountSetupStep ref={stepContainerRef} {...stepProps} />}
          {currentStepId === 'audience' && <AudienceStep ref={stepContainerRef} {...stepProps} />}
          {currentStepId === 'industry' && <IndustryStep ref={stepContainerRef} {...stepProps} />}
          {currentStepId === 'markets' && <MarketsStep ref={stepContainerRef} {...stepProps} />}
          {currentStepId === 'jtbd' && <JtbdStep ref={stepContainerRef} {...stepProps} />}
          {currentStepId === 'brand' && (
            <BrandStep
              ref={stepContainerRef}
              {...stepProps}
              onSubStepChange={handleSubStepChange}
            />
          )}
          {currentStepId === 'getting_started' && (
            <GettingStartedGuide ref={stepContainerRef} {...stepProps} />
          )}

          {completionError && (
            <div
              role="alert"
              className="mt-4 flex items-center justify-center gap-2 text-destructive"
            >
              <span className="text-sm">{completionError}</span>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </OnboardingLayout>
  )
}
